import React, { useEffect, useState } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCheckbox,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { time, chatbubble } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import IvrConfigService from './services/ivr-config.service';
import { PhoneNumber } from './interfaces/phone-number.interface';
import { IvrConfiguration, IvrFlowObject } from './interfaces/ivr-configuration.interface';
import MessageEditor from './components/MessageEditor';

const TIME_OPTIONS = [
  { id: '0', text: '00:00' }, { id: '1', text: '00:29' }, { id: '2', text: '00:30' }, { id: '3', text: '00:59' },
  { id: '4', text: '01:00' }, { id: '5', text: '01:29' }, { id: '6', text: '01:30' }, { id: '7', text: '01:59' },
  { id: '8', text: '02:00' }, { id: '9', text: '02:29' }, { id: '10', text: '02:30' }, { id: '11', text: '02:59' },
  { id: '12', text: '03:00' }, { id: '13', text: '03:29' }, { id: '14', text: '03:30' }, { id: '15', text: '03:59' },
  { id: '16', text: '04:00' }, { id: '17', text: '04:29' }, { id: '18', text: '04:30' }, { id: '19', text: '04:59' },
  { id: '20', text: '05:00' }, { id: '21', text: '05:29' }, { id: '22', text: '05:30' }, { id: '23', text: '05:59' },
  { id: '24', text: '06:00' }, { id: '25', text: '06:29' }, { id: '26', text: '06:30' }, { id: '27', text: '06:59' },
  { id: '28', text: '07:00' }, { id: '29', text: '07:29' }, { id: '30', text: '07:30' }, { id: '31', text: '07:59' },
  { id: '32', text: '08:00' }, { id: '33', text: '08:29' }, { id: '34', text: '08:30' }, { id: '35', text: '08:59' },
  { id: '36', text: '09:00' }, { id: '37', text: '09:29' }, { id: '38', text: '09:30' }, { id: '39', text: '09:59' },
  { id: '40', text: '10:00' }, { id: '41', text: '10:29' }, { id: '42', text: '10:30' }, { id: '43', text: '10:59' },
  { id: '44', text: '11:00' }, { id: '45', text: '11:29' }, { id: '46', text: '11:30' }, { id: '47', text: '11:59' },
  { id: '48', text: '12:00' }, { id: '49', text: '12:29' }, { id: '50', text: '12:30' }, { id: '51', text: '12:59' },
  { id: '52', text: '13:00' }, { id: '53', text: '13:29' }, { id: '54', text: '13:30' }, { id: '55', text: '13:59' },
  { id: '56', text: '14:00' }, { id: '57', text: '14:29' }, { id: '58', text: '14:30' }, { id: '59', text: '14:59' },
  { id: '60', text: '15:00' }, { id: '61', text: '15:29' }, { id: '62', text: '15:30' }, { id: '63', text: '15:59' },
  { id: '64', text: '16:00' }, { id: '65', text: '16:29' }, { id: '66', text: '16:30' }, { id: '67', text: '16:59' },
  { id: '68', text: '17:00' }, { id: '69', text: '17:29' }, { id: '70', text: '17:30' }, { id: '71', text: '17:59' },
  { id: '72', text: '18:00' }, { id: '73', text: '18:29' }, { id: '74', text: '18:30' }, { id: '75', text: '18:59' },
  { id: '76', text: '19:00' }, { id: '77', text: '19:29' }, { id: '78', text: '19:30' }, { id: '1945', text: '19:45' }, { id: '79', text: '19:59' },
  { id: '80', text: '20:00' }, { id: '81', text: '20:29' }, { id: '82', text: '20:30' }, { id: '83', text: '20:59' },
  { id: '84', text: '21:00' }, { id: '85', text: '21:29' }, { id: '86', text: '21:30' }, { id: '87', text: '21:59' },
  { id: '88', text: '22:00' }, { id: '89', text: '22:29' }, { id: '90', text: '22:30' }, { id: '91', text: '22:59' },
  { id: '92', text: '23:00' }, { id: '93', text: '23:29' }, { id: '94', text: '23:30' }, { id: '95', text: '23:59' },
];

interface State {
  organizationId: string;
}

const IvrConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<State | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<string | null>(null);
  const [ivrConfig, setIvrConfig] = useState<IvrConfiguration | null>(null);
  const [originalConfig, setOriginalConfig] = useState<IvrConfiguration | null>(null);
  const [selectedObject, setSelectedObject] = useState<IvrFlowObject | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [localTransferNumber, setLocalTransferNumber] = useState('');
  const [messagesNeedingAudio, setMessagesNeedingAudio] = useState<Set<string>>(new Set());
  const [hidePhoneSelector, setHidePhoneSelector] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string; color: string }>({
    show: false,
    message: '',
    color: 'success',
  });

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const organizationId = urlSearchParams.get('organizationId');
    const phoneNumber = urlSearchParams.get('phoneNumber');

    if (!organizationId) {
      setToast({
        show: true,
        message: 'Organization ID is required',
        color: 'danger',
      });
      return;
    }

    setState({ organizationId });
    
    // If phoneNumber parameter is provided, we'll auto-select it after loading phone numbers
    if (phoneNumber) {
      sessionStorage.setItem('autoSelectPhoneNumber', phoneNumber);
      setHidePhoneSelector(true);
    }
  }, []);

  useEffect(() => {
    if (!state?.organizationId) return;

    setLoading(true);
    IvrConfigService.getPhoneNumbers(state.organizationId)
      .then((numbers) => {
        const ivrNumbers = numbers.filter((num) => 
          num.number_flow_type === 'IVR' && num.group_name !== 'Transferências Ligo IVR'
        );
        setPhoneNumbers(ivrNumbers);
        
        // Auto-select phone number if parameter was provided
        const autoSelectNumber = sessionStorage.getItem('autoSelectPhoneNumber');
        console.log(autoSelectNumber, ivrNumbers);
        if (autoSelectNumber) {
          const matchedNumber = ivrNumbers.find(num => num.external_did_masked.includes(autoSelectNumber));
          if (matchedNumber) {
            console.log('matchedNumber', matchedNumber);
            handlePhoneNumberChange(matchedNumber._id);
            sessionStorage.removeItem('autoSelectPhoneNumber');
          }
        }
      })
      .catch((error) => {
        console.error('Failed to fetch phone numbers:', error);
        setToast({
          show: true,
          message: 'Failed to load phone numbers',
          color: 'danger',
        });
      })
      .finally(() => setLoading(false));
  }, [state]);

  const handlePhoneNumberChange = (phoneNumberId: string) => {
    if (!state?.organizationId || !phoneNumberId) return;

    setSelectedPhoneNumberId(phoneNumberId);
    setSelectedObject(null);
    setLoadingConfig(true);

    IvrConfigService.getIvrConfiguration(state.organizationId, phoneNumberId)
      .then((config) => {
        setIvrConfig(config);
        setOriginalConfig(JSON.parse(JSON.stringify(config)));
      })
      .catch((error) => {
        console.error('Failed to fetch IVR configuration:', error);
        setToast({
          show: true,
          message: 'Failed to load IVR configuration',
          color: 'danger',
        });
      })
      .finally(() => setLoadingConfig(false));
  };

  // Sync local transfer number when selectedObject changes
  useEffect(() => {
    if (selectedObject?.type === 'clock') {
      setLocalTransferNumber(selectedObject.service_Id || '');
    }
  }, [selectedObject?.id]);

  const handleObjectUpdate = (updatedObject: IvrFlowObject) => {
    if (!ivrConfig) return;

    const updatedConfig = { ...ivrConfig };
    let objectFound = false;

    updatedConfig.flow = updatedConfig.flow.map((line) => ({
      ...line,
      objects: line.objects.map((obj) => {
        if (obj.id === updatedObject.id) {
          objectFound = true;
          return updatedObject;
        }
        return obj;
      }),
    }));

    if (objectFound) {
      setIvrConfig(updatedConfig);
      setSelectedObject(updatedObject);
    }
  };

  const handleTransferNumberChange = (value: string) => {
    // Only allow digits
    const numbersOnly = value.replace(/[^0-9]/g, '');
    setLocalTransferNumber(numbersOnly);
  };

  const handleTransferNumberBlur = () => {
    if (selectedObject && localTransferNumber !== selectedObject.service_Id) {
      handleObjectUpdate({
        ...selectedObject,
        service_Id: localTransferNumber,
      });
    }
  };

  const handleAudioRegenerationNeeded = React.useCallback((objectId: string, needed: boolean) => {
    setMessagesNeedingAudio(prev => {
      const newSet = new Set(prev);
      if (needed) {
        newSet.add(objectId);
      } else {
        newSet.delete(objectId);
      }
      return newSet;
    });
  }, []);

  const commitLocalChanges = () => {
    // Commit any uncommitted local transfer number changes
    if (selectedObject?.type === 'clock' && localTransferNumber !== (selectedObject.service_Id || '')) {
      handleObjectUpdate({
        ...selectedObject,
        service_Id: localTransferNumber,
      });
    }
  };

  const handleSave = async () => {
    if (!state?.organizationId || !ivrConfig) return;

    // Commit any uncommitted local changes before saving
    commitLocalChanges();

    setSaving(true);
    setSaveError(false);

    try {
      // Use a slight delay to ensure state updates are processed
      await new Promise(resolve => setTimeout(resolve, 100));

      await IvrConfigService.updateIvrConfiguration(state.organizationId, ivrConfig);
      setOriginalConfig(JSON.parse(JSON.stringify(ivrConfig)));
      setToast({
        show: true,
        message: t('CONFIGURATION_SAVED_SUCCESSFULLY'),
        color: 'success',
      });
      if (window.parent !== window) {
        window.parent.postMessage(
          { type: 'IVR_SAVE_SUCCESS', message: t('CONFIGURATION_SAVED_SUCCESSFULLY') },
          '*'
        );
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setSaveError(true);
      setToast({
        show: true,
        message: t('FAILED_TO_SAVE_CONFIGURATION'),
        color: 'danger',
      });
      if (window.parent !== window) {
        window.parent.postMessage(
          { type: 'IVR_SAVE_ERROR', message: t('FAILED_TO_SAVE_CONFIGURATION') },
          '*'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalConfig) {
      setIvrConfig(JSON.parse(JSON.stringify(originalConfig)));
      setSelectedObject(null);
      setToast({
        show: true,
        message: 'Changes discarded',
        color: 'medium',
      });
    }
  };

  const handleReset = () => {
    if (selectedPhoneNumberId && state?.organizationId) {
      handlePhoneNumberChange(selectedPhoneNumberId);
    }
  };

  const getSelectedPhoneNumber = (): PhoneNumber | undefined => {
    return phoneNumbers.find((num) => num._id === selectedPhoneNumberId);
  };

  const formatPhoneNumber = (phone: PhoneNumber): string => {
    return `+${phone.ddi_number} ${phone.ddd_number} ${phone.telephone_number}`;
  };

  const hasChanges = (): boolean => {
    // Check if there are uncommitted local changes
    const hasLocalTransferNumberChange = 
      selectedObject?.type === 'clock' && 
      localTransferNumber !== (selectedObject.service_Id || '');
    
    // Check if main config has changed
    const hasConfigChange = JSON.stringify(ivrConfig) !== JSON.stringify(originalConfig);
    
    // Check if any messages need audio regeneration (block save if true)
    const hasMessagesNeedingAudio = messagesNeedingAudio.size > 0;
    
    return (hasConfigChange || hasLocalTransferNumberChange) && !hasMessagesNeedingAudio;
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <IonToast
          isOpen={toast.show}
          onDidDismiss={() => setToast({ ...toast, show: false })}
          message={toast.message}
          duration={3000}
          color={toast.color}
        />

        <IonGrid style={{ padding: '0 16px' }}>
          {!hidePhoneSelector && (
            <IonRow>
              <IonCol size="12">
                {loading && !ivrConfig ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <IonSpinner name="crescent" />
                  </div>
                ) : phoneNumbers.length === 0 ? (
                  <IonText color="medium" style={{ fontSize: '14px' }}>
                    <p>{t('NO_IVR_PHONE_NUMBERS_FOUND')}</p>
                  </IonText>
                ) : (
                  <div>
                    <IonText>
                      <h3 style={{ fontSize: '16px', margin: '8px 0' }}>{t('SELECT_PHONE_NUMBER')}</h3>
                    </IonText>
                    <IonGrid style={{ padding: 0 }}>
                      <IonRow>
                        {phoneNumbers.map((phone) => (
                          <IonCol key={phone._id} size="12" sizeSm="6" sizeMd="4">
                            <div
                              onClick={() => handlePhoneNumberChange(phone._id)}
                              style={{
                                border: selectedPhoneNumberId === phone._id 
                                  ? '2px solid var(--ion-color-primary)' 
                                  : '1px solid var(--ion-color-light)',
                                borderRadius: '8px',
                                padding: '12px',
                                cursor: 'pointer',
                                backgroundColor: 'transparent',
                                transition: 'all 0.2s',
                                margin: '4px 0',
                              }}
                            >
                              <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                {formatPhoneNumber(phone)}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--ion-color-medium)', marginTop: '4px' }}>
                                {phone.group_name}
                              </div>
                            </div>
                          </IonCol>
                        ))}
                      </IonRow>
                    </IonGrid>
                  </div>
                )}
              </IonCol>
            </IonRow>
          )}

          {selectedPhoneNumberId && (
            <>
              {loadingConfig ? (
                <IonRow>
                  <IonCol size="12">
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <IonSpinner name="crescent" />
                    </div>
                  </IonCol>
                </IonRow>
              ) : ivrConfig ? (
                <IonRow>
                  <IonCol size="12">
                    <IonCard style={{ margin: '4px 0', width: '100%', alignItems: 'flex-start', height: 'auto' }}>
                      <IonCardContent style={{ padding: '12px', width: '100%' }}>
                        <IonGrid style={{ padding: 0 }}>
                          <IonRow>
                            <IonCol size="12" sizeMd="4">
                              <div style={{ textAlign: 'left' }}>
                                <IonText>
                                  <h3 style={{ fontSize: '16px', margin: '0 0 12px 0' }}>{t('CONFIGURATION_STEPS')}</h3>
                                </IonText>
                                <IonText color="medium">
                                  <p style={{ marginBottom: '12px', fontSize: '14px' }}>
                                    {t('CLICK_STEP_TO_EDIT')}
                                  </p>
                                </IonText>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {ivrConfig.flow.flatMap(line => line.objects)
                                    .filter(obj => obj.type === 'clock' || obj.type === 'message')
                                    .map((object, index) => {
                                      const isSelected = object.id === selectedObject?.id;
                                      const label = object.type === 'clock' ? t('EDIT_SCHEDULE') : t('EDIT_MESSAGE');
                                      const subtypeKey = object.subtype ? object.subtype.toUpperCase() : '';
                                      const description = subtypeKey ? `(${t(subtypeKey)})` : '';
                                      const iconToUse = object.type === 'clock' ? time : chatbubble;
                                      
                                      return (
                                        <div
                                          key={object.id}
                                          onClick={() => setSelectedObject(object)}
                                          style={{
                                            cursor: 'pointer',
                                            border: isSelected ? '2px solid var(--ion-color-primary)' : 'none',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            backgroundColor: 'transparent',
                                            transition: 'all 0.2s',
                                          }}
                                        >
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <IonIcon
                                              icon={iconToUse}
                                              style={{ fontSize: '24px', color: 'var(--ion-color-primary)' }}
                                            />
                                            <div style={{ flex: 1 }}>
                                              <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                                {index + 1}. {label}
                                              </div>
                                              {description && (
                                                <div style={{ fontSize: '13px', color: 'var(--ion-color-medium)' }}>
                                                  {description}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            </IonCol>

                            <IonCol size="12" sizeMd="8">
                              <div style={{ textAlign: 'left' }}>
                                {selectedObject ? (
                                  selectedObject.type === 'clock' ? (
                                    <div>
                                      <IonText>
                                        <h3 style={{ fontSize: '16px', margin: '0 0 12px 0' }}>{t('SCHEDULE_CONFIGURATION')}</h3>
                                      </IonText>
                                  <IonGrid style={{ padding: 0 }}>
                                    <IonRow>
                                      {selectedObject.timezone && (
                                        <IonCol size="12" sizeMd="6">
                                          <IonItem lines="none">
                                            <IonLabel>
                                              <strong>{t('TIMEZONE')}:</strong> {selectedObject.timezone}
                                            </IonLabel>
                                          </IonItem>
                                        </IonCol>
                                      )}
                                      <IonCol size="12" sizeMd={selectedObject.timezone ? "6" : "12"}>
                                        <IonItem>
                                          <IonLabel position="floating">{t('TRANSFER_NUMBER_WHEN_CLOSED')}</IonLabel>
                                          <IonInput
                                            type="tel"
                                            value={localTransferNumber}
                                            onIonChange={(e) => handleTransferNumberChange(e.detail.value!)}
                                            onIonBlur={handleTransferNumberBlur}
                                            placeholder="Enter phone number"
                                          />
                                        </IonItem>
                                      </IonCol>
                                    </IonRow>
                                  </IonGrid>

                                  {(selectedObject.schedule || []).map((schedule, scheduleIndex) => (
                                    <div key={schedule.id} style={{ marginTop: '12px', border: '1px solid var(--ion-color-light)', borderRadius: '8px', padding: '12px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <IonText>
                                          <strong style={{ fontSize: '14px' }}>{t('SCHEDULE')} {scheduleIndex + 1}</strong>
                                        </IonText>
                                        {(selectedObject.schedule || []).length > 1 && (
                                          <IonButton
                                            color="danger"
                                            size="small"
                                            fill="clear"
                                            onClick={() => {
                                              const updatedSchedules = (selectedObject.schedule || []).filter((_, index) => index !== scheduleIndex);
                                              handleObjectUpdate({
                                                ...selectedObject,
                                                schedule: updatedSchedules,
                                              });
                                            }}
                                          >
                                            {t('REMOVE')}
                                          </IonButton>
                                        )}
                                      </div>

                                      <IonGrid style={{ padding: 0 }}>
                                        <IonRow>
                                          <IonCol size="12">
                                            <IonLabel style={{ fontSize: '13px' }}>
                                              <strong>{t('DAYS')}:</strong>
                                            </IonLabel>
                                            <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                                              {[
                                                { id: 0, key: 'SUNDAY' },
                                                { id: 1, key: 'MONDAY' },
                                                { id: 2, key: 'TUESDAY' },
                                                { id: 3, key: 'WEDNESDAY' },
                                                { id: 4, key: 'THURSDAY' },
                                                { id: 5, key: 'FRIDAY' },
                                                { id: 6, key: 'SATURDAY' },
                                              ].map((day) => {
                                                const weekDay = schedule.week.find((w) => w.id === day.id);
                                                return (
                                                  <IonItem key={day.id} lines="none" style={{ '--padding-start': '0', '--inner-padding-end': '0', minWidth: 'auto' }}>
                                                    <IonCheckbox
                                                      checked={weekDay?.state || false}
                                                      onIonChange={() => {
                                                        const updatedSchedules = [...(selectedObject.schedule || [])];
                                                        const dayIndex = updatedSchedules[scheduleIndex].week.findIndex((d) => d.id === day.id);
                                                        updatedSchedules[scheduleIndex].week[dayIndex].state = !updatedSchedules[scheduleIndex].week[dayIndex].state;
                                                        handleObjectUpdate({
                                                          ...selectedObject,
                                                          schedule: updatedSchedules,
                                                        });
                                                      }}
                                                      style={{ marginRight: '4px' }}
                                                    />
                                                    <IonLabel style={{ fontSize: '13px' }}>{t(day.key)}</IonLabel>
                                                  </IonItem>
                                                );
                                              })}
                                            </div>
                                          </IonCol>
                                        </IonRow>
                                        <IonRow style={{ marginTop: '8px' }}>
                                          <IonCol size="6">
                                            <IonItem>
                                              <IonLabel position="floating" style={{ fontSize: '13px' }}>{t('OPENING')}</IonLabel>
                                              <IonSelect
                                                value={schedule.open.id}
                                                onIonChange={(e) => {
                                                  const selectedOption = TIME_OPTIONS.find(opt => opt.id === e.detail.value);
                                                  if (selectedOption) {
                                                    const updatedSchedules = [...(selectedObject.schedule || [])];
                                                    updatedSchedules[scheduleIndex].open = {
                                                      id: selectedOption.id,
                                                      text: selectedOption.text,
                                                    };
                                                    handleObjectUpdate({
                                                      ...selectedObject,
                                                      schedule: updatedSchedules,
                                                    });
                                                  }
                                                }}
                                              >
                                                {TIME_OPTIONS.map(option => (
                                                  <IonSelectOption key={option.id} value={option.id}>
                                                    {option.text}
                                                  </IonSelectOption>
                                                ))}
                                              </IonSelect>
                                            </IonItem>
                                          </IonCol>
                                          <IonCol size="6">
                                            <IonItem>
                                              <IonLabel position="floating" style={{ fontSize: '13px' }}>{t('CLOSING')}</IonLabel>
                                              <IonSelect
                                                value={schedule.closed.id}
                                                onIonChange={(e) => {
                                                  const selectedOption = TIME_OPTIONS.find(opt => opt.id === e.detail.value);
                                                  if (selectedOption) {
                                                    const updatedSchedules = [...(selectedObject.schedule || [])];
                                                    updatedSchedules[scheduleIndex].closed = {
                                                      id: selectedOption.id,
                                                      text: selectedOption.text,
                                                    };
                                                    handleObjectUpdate({
                                                      ...selectedObject,
                                                      schedule: updatedSchedules,
                                                    });
                                                  }
                                                }}
                                              >
                                                {TIME_OPTIONS.map(option => (
                                                  <IonSelectOption key={option.id} value={option.id}>
                                                    {option.text}
                                                  </IonSelectOption>
                                                ))}
                                              </IonSelect>
                                            </IonItem>
                                          </IonCol>
                                        </IonRow>
                                      </IonGrid>
                                    </div>
                                  ))}

                                  <IonButton 
                                    expand="block" 
                                    onClick={() => {
                                      const newSchedule = {
                                        id: (selectedObject.schedule || []).length + 1,
                                        week: [
                                          { id: 0, name: 'D', state: false },
                                          { id: 1, name: 'S', state: false },
                                          { id: 2, name: 'T', state: false },
                                          { id: 3, name: 'Q', state: false },
                                          { id: 4, name: 'Q', state: false },
                                          { id: 5, name: 'S', state: false },
                                          { id: 6, name: 'S', state: false },
                                        ],
                                        open: { id: '0', text: '08:00' },
                                        closed: { id: '0', text: '18:00' },
                                      };
                                      handleObjectUpdate({
                                        ...selectedObject,
                                        schedule: [...(selectedObject.schedule || []), newSchedule],
                                      });
                                    }}
                                    style={{ marginTop: '16px' }}
                                  >
                                    {t('ADD_SCHEDULE_BLOCK')}
                                  </IonButton>
                                </div>
                              ) : state?.organizationId ? (
                                <MessageEditor
                                  messageObject={selectedObject}
                                  organizationId={state.organizationId}
                                  onUpdate={handleObjectUpdate}
                                  onAudioRegenerationNeeded={handleAudioRegenerationNeeded}
                                />
                              ) : null
                            ) : (
                              <div style={{ padding: '40px 20px', color: 'var(--ion-color-medium)' }}>
                                <IonText>
                                  <p>{t('SELECT_STEP_TO_EDIT')}</p>
                                </IonText>
                              </div>
                            )}
                              </div>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                </IonRow>
              ) : null}
            </>
          )}

          {ivrConfig && (
            <IonRow>
              <IonCol size="12">
                {ivrConfig && (
                  <>
                    {messagesNeedingAudio.size > 0 && (
                      <div style={{ 
                        margin: '12px 8px 0 8px',
                        padding: '12px 16px', 
                        backgroundColor: 'var(--ion-color-warning-tint)', 
                        borderLeft: '4px solid var(--ion-color-warning)',
                        borderRadius: '4px'
                      }}>
                        <p style={{ margin: 0, fontSize: '14px', color: '#000', fontWeight: 500 }}>
                          ⚠️ {t('AUDIO_REGENERATION_REQUIRED')}
                        </p>
                      </div>
                    )}
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      justifyContent: 'flex-end', 
                      padding: '12px 8px',
                      position: 'sticky',
                      bottom: 0,
                      backgroundColor: 'var(--ion-background-color)',
                      borderTop: '1px solid var(--ion-color-light)',
                      zIndex: 10
                    }}>
                      <IonButton
                        color="light"
                        onClick={handleReset}
                        disabled={saving}
                        size="default"
                      >
                        {t('RESET')}
                      </IonButton>
                      <IonButton
                        color="medium"
                        onClick={handleCancel}
                        disabled={saving || !hasChanges()}
                        size="default"
                      >
                        {t('CANCEL')}
                      </IonButton>
                      <IonButton
                        color="primary"
                        onClick={handleSave}
                        disabled={saving || !hasChanges()}
                        size="default"
                      >
                        {saving ? (
                          <>
                            <IonSpinner name="crescent" style={{ width: '16px', height: '16px' }} />
                            <span style={{ marginLeft: '8px' }}>{t('SAVING')}</span>
                          </>
                        ) : (
                          t('SAVE_CHANGES')
                        )}
                      </IonButton>
                    </div>
                  </>
                )}
              </IonCol>
            </IonRow>
          )}
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default IvrConfigPage;
